import React, { useState } from "react";
import { withApollo } from "react-apollo";
import queries from "../../../queries";
import ImageUploader from "../../uploaders/ImageUploader";
import HtmlDescriptionEditor from "./HtmlDescriptionEditor";

// Editing of the events of an Agenda 2.0 cell: time, texts, popup, category and day of each card.
// Times are wall-clock times (the display shows them exactly as typed here).

// Couleurs proposées pour le titre d'un évènement (le violet est celui des thèmes Euroseeds, agenda2Themes.js)
const TITLE_COLORS = [['Violet Euroseeds', '#654455'], ['Vert Euroseeds', '#A6C82A'], ['Noir', '#000000'], ['Blanc', '#FFFFFF']];

const POPUP_OPTIONS = [
  ['auto', "Automatique (un popup s'il y a une description ou une image)"],
  ['yes', 'Oui : la carte ouvre un popup'],
  ['no', 'Non : pas de popup'],
];

const dayLabel = (day) => {
  const [year, month, date] = day.date.slice(0, 10).split('-');
  return `${date}.${month}.${year}`;
};

const timeOf = (isoDate) => isoDate.slice(11, 16);

// Imported events keep their status ("Closed meeting") in the description; the card then shows it next to the
// title. The form moves it to the "text next to the title" field so both can be edited separately.
const legacyStatus = (event) => {
  const hasSubtitle = event.subtitle !== null && event.subtitle !== undefined;
  const description = event.description || '';
  return !hasSubtitle && description.length > 0 && description.length <= 80 && !/<[^>]+>/.test(description) && !description.includes('\n')
    ? description
    : null;
};

const popupToOption = (popup) => (popup === true ? 'yes' : popup === false ? 'no' : 'auto');
const optionToPopup = (option) => (option === 'yes' ? true : option === 'no' ? false : null);

function EventForm({ event, days, categories, open, onToggle, onSave, onDelete }) {
  const [form, setForm] = useState(() => ({
    name: event.name || '',
    agenda_day_id: event.agenda_day_id,
    start: timeOf(event.start),
    end: timeOf(event.end),
    subtitle: legacyStatus(event) !== null ? legacyStatus(event) : (event.subtitle === null || event.subtitle === undefined ? '' : event.subtitle),
    description: legacyStatus(event) !== null ? '' : (event.description || ''),
    speaker: event.speaker || '',
    location: event.location || '',
    language: event.language || '',
    image_uri: event.image_uri || '',
    popup: popupToOption(event.popup),
    category_id: event.category_id || '',
    title_bold: event.title_bold || false,
    title_italic: event.title_italic || false,
    title_font_family: event.title_font_family || '',
    title_color: event.title_color || '',
    title_font_size: event.title_font_size || '',
  }));
  const [subtitleTouched, setSubtitleTouched] = useState(legacyStatus(event) !== null);
  const [state, setState] = useState('');
  const set = (key, value) => { setForm((previous) => ({ ...previous, [key]: value })); setState(''); };

  const save = async () => {
    setState('Enregistrement…');
    try {
      const day = days.find((d) => d.id === form.agenda_day_id);
      const date = day.date.slice(0, 10);
      const input = {
        id: event.id,
        name: form.name,
        agenda_day_id: form.agenda_day_id,
        start: `${date}T${form.start}`,
        end: `${date}T${form.end}`,
        description: form.description,
        speaker: form.speaker,
        location: form.location,
        language: form.language,
        image_uri: form.image_uri,
        popup: optionToPopup(form.popup),
        category_id: form.category_id || null,
        title_bold: form.title_bold,
        title_italic: form.title_italic,
        title_font_family: form.title_font_family,
        title_color: form.title_color || null,
        title_font_size: form.title_font_size === '' ? null : parseFloat(form.title_font_size),
      };
      // an untouched empty subtitle stays "automatic" (a short description is then shown as the status)
      if (subtitleTouched || form.subtitle) input.subtitle = form.subtitle;
      await onSave(input);
      setState('Enregistré');
    } catch (error) {
      setState('Erreur : ' + error.message);
    }
  };

  const field = (label, control) => (
    <div className="tsk-input-line">
      <label>{label}</label>
      {control}
    </div>
  );
  const text = (key, label, placeholder) => field(label, (
    <input type="text" value={form[key]} placeholder={placeholder} onChange={(e) => set(key, e.target.value)} />
  ));
  const checkbox = (key, label) => field(label, (
    <input type="checkbox" checked={form[key]} onChange={(e) => set(key, e.target.checked)} />
  ));

  return (
    <div style={{ border: '1px solid var(--tsk-border)', borderRadius: 4, marginBottom: 8, background: 'var(--tsk-bg-card)', color: 'var(--tsk-text)' }}>
      <div onClick={onToggle} style={{ cursor: 'pointer', padding: '8px 10px', display: 'flex', gap: 10, alignItems: 'baseline' }}>
        <strong style={{ whiteSpace: 'nowrap' }}>{timeOf(event.start).replace(':', '.')} – {timeOf(event.end).replace(':', '.')}</strong>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{event.name}</span>
        <span aria-hidden="true">{open ? '▾' : '▸'}</span>
      </div>
      {open &&
        <div style={{ padding: '4px 10px 10px' }}>
          {text('name', 'Titre')}
          {checkbox('title_bold', 'Titre en gras')}
          {checkbox('title_italic', 'Titre en italique')}
          {field('Couleur du titre (aucune = celle de la cellule)', (
            <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              {TITLE_COLORS.map(([name, hex]) => (
                <button
                  type="button" key={hex} title={name} aria-label={name} onClick={() => set('title_color', hex)}
                  style={{ width: 28, height: 28, padding: 0, borderRadius: '50%', cursor: 'pointer', background: hex, border: form.title_color.toLowerCase() === hex.toLowerCase() ? '3px solid var(--tsk-teal, #4f46e5)' : '1px solid var(--tsk-border)' }}
                />
              ))}
              <input
                type="color" title="Autre couleur" aria-label="Autre couleur"
                value={/^#[0-9a-f]{6}$/i.test(form.title_color) ? form.title_color : '#000000'}
                onChange={(e) => set('title_color', e.target.value)}
              />
              <button type="button" className="tsk-button" title="Pas de couleur propre : le titre prend la couleur de la cellule" onClick={() => set('title_color', '')} disabled={!form.title_color}>Aucune couleur</button>
            </span>
          ))}
          {text('title_font_family', 'Police du titre', "Ex. : Georgia (laisser vide pour la police par défaut)")}
          {field('Taille du titre (relatif, ex. 1.4)', (
            <input
              type="number" step="0.1" min="0" value={form.title_font_size}
              placeholder="Défaut"
              onChange={(e) => set('title_font_size', e.target.value)}
            />
          ))}
          {field('Jour', (
            <select value={form.agenda_day_id} onChange={(e) => set('agenda_day_id', e.target.value)}>
              {days.map((day) => <option key={day.id} value={day.id}>{dayLabel(day)}</option>)}
            </select>
          ))}
          {field('Heure de début', <input type="time" value={form.start} onChange={(e) => set('start', e.target.value)} />)}
          {field('Heure de fin', <input type="time" value={form.end} onChange={(e) => set('end', e.target.value)} />)}
          {field('Texte à côté du titre', (
            <input
              type="text" value={form.subtitle} placeholder="Ex. : Closed meeting"
              onChange={(e) => { setSubtitleTouched(true); set('subtitle', e.target.value); }}
            />
          ))}
          {categories.length > 0 && field('Catégorie', (
            <select value={form.category_id} onChange={(e) => set('category_id', e.target.value)}>
              <option value="">Aucune</option>
              {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
            </select>
          ))}
          {text('speaker', 'Intervenant')}
          {text('location', 'Lieu')}
          {text('language', 'Langue')}
          {field('Popup', (
            <select value={form.popup} onChange={(e) => set('popup', e.target.value)}>
              {POPUP_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          ))}
          {field('Description du popup', (
            <HtmlDescriptionEditor value={form.description} onChange={(html) => set('description', html)} />
          ))}
          <div className="tsk-input-line tsk-input-line--full">
            <label>Image du popup</label>
            <ImageUploader
              value={form.image_uri}
              canDelete
              onUpload={(uri) => set('image_uri', uri)}
              onDelete={() => set('image_uri', '')}
            />
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8 }}>
            <button type="button" className="tsk-button" onClick={save}>Enregistrer</button>
            <button
              type="button" className="tsk-button"
              onClick={() => { if (window.confirm('Supprimer cet évènement ?')) onDelete(); }}
            >
              Supprimer
            </button>
            <span>{state}</span>
          </div>
        </div>
      }
    </div>
  );
}

function Agendas2EventsEditor({ cell, refetch, client }) {
  const days = [...cell.days].sort((a, b) => a.date.localeCompare(b.date));
  const [pickedDay, setPickedDay] = useState(null);
  const [openId, setOpenId] = useState(null);
  const dayId = days.some((day) => day.id === pickedDay) ? pickedDay : (days[0] && days[0].id);

  if (days.length === 0) {
    return <p>Aucun jour pour l'instant : ajoute un jour dans la section « Jours », ou importe un CSV.</p>;
  }

  const events = cell.events
    .filter((event) => event.agenda_day_id === dayId)
    .sort((a, b) => a.start.localeCompare(b.start) || a.end.localeCompare(b.end));

  const run = async (mutation, variables) => {
    await client.mutate({ mutation, variables });
    await refetch();
  };

  const add = async () => {
    const day = days.find((d) => d.id === dayId);
    const date = day.date.slice(0, 10);
    const result = await client.mutate({
      mutation: queries.cells.agendas2.addEvent,
      variables: {
        input: {
          cell_id: cell.id,
          agenda_day_id: dayId,
          name: 'Nouvel évènement',
          start: `${date}T09:00`,
          end: `${date}T10:00`,
          category_id: cell.categories.length === 1 ? cell.categories[0].id : null,
        },
      },
    });
    await refetch();
    setOpenId(result.data.addAgendaEvent.id);
  };

  return (
    <div>
      <div className="tsk-input-line">
        <label>Jour</label>
        <select value={dayId} onChange={(e) => setPickedDay(e.target.value)}>
          {days.map((day) => <option key={day.id} value={day.id}>{dayLabel(day)}</option>)}
        </select>
      </div>
      {events.length === 0 && <p>Aucun évènement ce jour-là.</p>}
      {events.map((event) => (
        <EventForm
          key={[event.id, event.start, event.end, event.name, event.subtitle, event.popup, event.description, event.image_uri, event.agenda_day_id, event.category_id, event.speaker, event.location, event.language, event.title_bold, event.title_italic, event.title_font_family, event.title_font_size, event.title_color].join('|')}
          event={event}
          days={days}
          categories={cell.categories}
          open={openId === event.id}
          onToggle={() => setOpenId(openId === event.id ? null : event.id)}
          onSave={(input) => run(queries.cells.agendas2.updateEvent, { input })}
          onDelete={() => run(queries.cells.agendas2.deleteEvent, { id: event.id })}
        />
      ))}
      <button type="button" className="tsk-button" onClick={add}>Ajouter un évènement</button>
    </div>
  );
}

export default withApollo(Agendas2EventsEditor);
